import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Database } from "@revelate/types";
import { format, parse } from "date-fns";
import { InfoIcon } from "lucide-react";
import { useId } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import {
  stateOfSalesV2Colors,
  stateOfSalesV2L1Layout,
  stateOfSalesV2LegendClassName,
  stateOfSalesV2LegendSwatchClassName,
} from "./visualTokens";

type Currency = Database["public"]["Tables"]["currencies"]["Row"] | null;

export type ForecastChartRow = {
  month: string;
  target: number;
  total_minus_risk: number;
  expected_close_v2: number | null;
  crm_forecast: number;
  won: number;
  actual_last_year: number | null;
  is_context: boolean;
};

interface ForecastChartProps {
  data: ForecastChartRow[];
  companyCurrency: Currency;
  accumulated?: boolean;
  future?: boolean;
  historical?: boolean;
  onAccumulatedChange?: (checked: boolean) => void;
  selectedPeriodStart?: string | null;
  position?: ForecastPositionData;
}

export type ForecastPositionData = {
  expectedFromOpenDeals: number;
  forecast: number;
  future: boolean;
  gapToTarget: number;
  historical: boolean;
  target: number;
  won: number;
};

type ForecastTooltipPayload = {
  payload: ForecastChartRow;
};

export function ForecastChart({
  accumulated,
  data,
  companyCurrency,
  future = false,
  historical = false,
  onAccumulatedChange,
  selectedPeriodStart,
  position,
}: ForecastChartProps) {
  const chartId = `forecast-${useId().replace(/:/g, "")}`;

  if (!data.length) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[10px] border border-dashed border-gray-300 bg-gray-50 px-6 text-center text-sm text-gray-500">
        No forecast records exist for the selected period and filters.
      </div>
    );
  }

  const hasPriorYearActual = data.some((row) => row.actual_last_year !== null);
  const hasExpectedCloseV2 = !historical && data.some(
    row => row.expected_close_v2 !== null && Number.isFinite(row.expected_close_v2),
  );
  const hasContextPeriod = Boolean(
    selectedPeriodStart && data.some((row) => row.is_context),
  );
  const selectedPeriodStartIndex = selectedPeriodStart
    ? data.findIndex((row) => row.month === selectedPeriodStart)
    : -1;
  const selectedPeriodOffset = hasContextPeriod
    && selectedPeriodStartIndex > 0
    && data.length > 1
    ? `${selectedPeriodStartIndex * 100 / (data.length - 1)}%`
    : "0%";
  const forecastStroke = hasContextPeriod
    ? `url(#${chartId}-forecast-stroke)`
    : "#1D819A";
  const targetStroke = hasContextPeriod
    ? `url(#${chartId}-target-stroke)`
    : "#231942";
  const priorYearStroke = hasContextPeriod
    ? `url(#${chartId}-prior-year-stroke)`
    : "#6B7280";
  const crmStroke = hasContextPeriod
    ? `url(#${chartId}-crm-stroke)`
    : "#9F86C0";

  return (
    <div className="space-y-4">
      {position && (
        <ForecastPosition
          alignedL1
          position={position}
          companyCurrency={companyCurrency}
        />
      )}

      <div className="space-y-2" data-testid="forecast-chart">
        {typeof accumulated === "boolean" && onAccumulatedChange && (
          <div
            className={stateOfSalesV2L1Layout.visualHeader}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <span aria-hidden="true" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Accumulated</span>
              <Switch
                aria-label="Accumulated"
                checked={accumulated}
                onCheckedChange={onAccumulatedChange}
              />
            </div>
          </div>
        )}
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
            >
            <defs>
              <linearGradient id={`${chartId}-forecast-stroke`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1D819A" stopOpacity={0.25} />
                <stop offset={selectedPeriodOffset} stopColor="#1D819A" stopOpacity={0.25} />
                <stop offset={selectedPeriodOffset} stopColor="#1D819A" stopOpacity={1} />
                <stop offset="100%" stopColor="#1D819A" stopOpacity={1} />
              </linearGradient>
              <linearGradient
                id={`${chartId}-target-stroke`}
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#231942" stopOpacity={0.2} />
                <stop offset={selectedPeriodOffset} stopColor="#231942" stopOpacity={0.2} />
                <stop offset={selectedPeriodOffset} stopColor="#231942" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#231942" stopOpacity={0.55} />
              </linearGradient>
              <linearGradient id={`${chartId}-prior-year-stroke`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6B7280" stopOpacity={0.25} />
                <stop offset={selectedPeriodOffset} stopColor="#6B7280" stopOpacity={0.25} />
                <stop offset={selectedPeriodOffset} stopColor="#6B7280" stopOpacity={1} />
                <stop offset="100%" stopColor="#6B7280" stopOpacity={1} />
              </linearGradient>
              <linearGradient id={`${chartId}-crm-stroke`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#9F86C0" stopOpacity={0.25} />
                <stop offset={selectedPeriodOffset} stopColor="#9F86C0" stopOpacity={0.25} />
                <stop offset={selectedPeriodOffset} stopColor="#9F86C0" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#9F86C0" stopOpacity={0.9} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#E5E7EB" />
            <XAxis
              dataKey="month"
              tickFormatter={(value) => format(parse(value, "yyyy-MM", new Date()), "MMM")}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(value) => formatCompactIsoCurrency(value, companyCurrency)}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickLine={false}
              axisLine={false}
              width={88}
              domain={[0, (maximum: number) => maximum * 1.08]}
            />
            <ChartTooltip
              content={(
                <ForecastTooltip
                  companyCurrency={companyCurrency}
                  future={future}
                  historical={historical}
                />
              )}
            />
            <Legend
              content={(
                <ForecastLegend
                  hasExpectedCloseV2={hasExpectedCloseV2}
                  hasPriorYearActual={hasPriorYearActual}
                  historical={historical}
                />
              )}
            />

            {hasContextPeriod && selectedPeriodStart && (
              <ReferenceLine
                x={selectedPeriodStart}
                stroke="#9CA3AF"
                strokeDasharray="4 4"
                strokeWidth={1.5}
              />
            )}

            <Line
              type="monotone"
              dataKey="target"
              stroke={targetStroke}
              strokeWidth={1.5}
              opacity={hasContextPeriod ? 1 : 0.55}
              dot={false}
              activeDot={false}
              strokeLinecap="round"
              strokeLinejoin="round"
              legendType="none"
            />
            <Line
              type="monotone"
              dataKey={historical ? "won" : "expected_close_v2"}
              stroke={forecastStroke}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 4 }}
              strokeLinecap="round"
              strokeLinejoin="round"
              legendType="none"
            />
            {!historical && (
              <Line
                type="monotone"
                dataKey="crm_forecast"
                stroke={crmStroke}
                strokeWidth={1.75}
                dot={false}
                activeDot={{ r: 3 }}
                strokeLinecap="round"
                strokeLinejoin="round"
                legendType="none"
              />
            )}
            {hasPriorYearActual && (
              <Line
                type="monotone"
                dataKey="actual_last_year"
                stroke={priorYearStroke}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                opacity={1}
                dot={false}
                connectNulls
                strokeLinecap="round"
                strokeLinejoin="round"
                legendType="none"
              />
            )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export function ForecastPosition({
  alignedL1 = false,
  position,
  companyCurrency,
  expectedLabel = "Expected to close",
  showBar = true,
  targetUnavailable = false,
}: {
  alignedL1?: boolean;
  position: ForecastPositionData;
  companyCurrency: Currency;
  expectedLabel?: "Expected from open deals" | "Expected to close";
  showBar?: boolean;
  targetUnavailable?: boolean;
}) {
  const maxPositionValue = Math.max(position.target, position.forecast, 1) * 1.04;
  const wonWidth = Math.min(100, position.won * 100 / maxPositionValue);
  const expectedWidth = Math.min(
    100 - wonWidth,
    position.expectedFromOpenDeals * 100 / maxPositionValue,
  );
  const targetPosition = Math.min(100, position.target * 100 / maxPositionValue);

  return (
    <>
      <div className={cn(
        "rounded-[8px] border border-gray-200 bg-gray-50/60 p-4",
        alignedL1 && stateOfSalesV2L1Layout.positionBand,
      )}>
        {position.historical ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <PositionMetric label="Won" value={position.won} companyCurrency={companyCurrency} />
            <PositionMetric label="Target" value={position.target} companyCurrency={companyCurrency} unavailable={targetUnavailable} />
            <PositionMetric
              label="Gap to target"
              value={position.gapToTarget}
              companyCurrency={companyCurrency}
              display="gap"
              info="The gap compares won revenue with the target for the selected period."
              tone={position.gapToTarget < 0 ? "negative" : position.gapToTarget > 0 ? "positive" : "neutral"}
              unavailable={targetUnavailable}
            />
          </div>
        ) : (
          <div className="grid gap-x-3 gap-y-2 xl:grid-cols-5">
            <p className="text-[11px] font-medium text-gray-500 xl:col-span-3">
              Forecast composition
            </p>
            <div aria-hidden="true" className="hidden xl:col-span-2 xl:block" />

            <div className="grid grid-cols-3 gap-3 xl:contents">
              <PositionMetric
                label="Won"
                value={position.won}
                companyCurrency={companyCurrency}
                unavailable={position.future && position.won === 0}
              />
              <PositionMetric
                label={expectedLabel}
                value={position.expectedFromOpenDeals}
                companyCurrency={companyCurrency}
              />
              <PositionMetric
                label="Forecast"
                value={position.forecast}
                companyCurrency={companyCurrency}
                emphasis
                info="Forecast combines won revenue with expected revenue from open deals in the selected period."
              />
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4 xl:contents">
              <PositionMetric label="Target" value={position.target} companyCurrency={companyCurrency} unavailable={targetUnavailable} />
              <PositionMetric
                label="Gap to target"
                value={position.gapToTarget}
                companyCurrency={companyCurrency}
                display="gap"
                info="The gap compares the target with the forecast, including both won revenue and expected revenue."
                tone={position.gapToTarget < 0 ? "negative" : position.gapToTarget > 0 ? "positive" : "neutral"}
                unavailable={targetUnavailable}
              />
            </div>
          </div>
        )}
      </div>

      {showBar && (
        <div className={cn(
          "space-y-2 rounded-[8px] border border-gray-200 bg-gray-50/60 px-4 py-3",
          alignedL1 && stateOfSalesV2L1Layout.compositionBand,
        )}>
          <p className="text-[11px] font-medium text-gray-500">
            Forecast position
          </p>
          <div className="relative h-3 rounded-full bg-white ring-1 ring-gray-200">
            <div
              className={cn(
                "absolute inset-y-0 left-0 rounded-l-full bg-[#231942]",
                position.historical && "rounded-r-full",
              )}
              style={{ width: `${wonWidth}%` }}
            />
            <div
              className={cn(
                "absolute inset-y-0 rounded-r-full bg-[#1D819A]/45",
                position.historical && "hidden",
              )}
              style={{ left: `${wonWidth}%`, width: `${expectedWidth}%` }}
            />
            <div
              className="absolute -top-1 h-5 w-px bg-[#231942]"
              style={{ left: `${targetPosition}%` }}
            />
          </div>
          <div
            aria-label="Forecast position legend"
            className={stateOfSalesV2LegendClassName}
          >
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cn(stateOfSalesV2LegendSwatchClassName, "bg-[#231942]")}
              />
              Won
            </span>
            {!position.historical && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn(stateOfSalesV2LegendSwatchClassName, "bg-[#1D819A]/45")}
                />
                {expectedLabel}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="h-4 w-px bg-[#231942]" />
              Target
            </span>
          </div>
        </div>
      )}
    </>
  );
}

function PositionMetric({
  label,
  value,
  companyCurrency,
  display = "currency",
  info,
  tone = "neutral",
  unavailable = false,
  emphasis = false,
}: {
  label: string;
  value: number;
  companyCurrency: Currency;
  display?: "currency" | "gap";
  info?: string;
  tone?: "neutral" | "positive" | "negative";
  unavailable?: boolean;
  emphasis?: boolean;
}) {
  const formattedValue = unavailable
    ? "—"
    : display === "gap"
    ? value < 0
      ? `${formatCompactIsoCurrency(Math.abs(value), companyCurrency)} below target`
      : value > 0
        ? `${formatCompactIsoCurrency(value, companyCurrency)} above target`
        : "On target"
    : formatCompactIsoCurrency(value, companyCurrency);

  return (
    <div className="min-w-0 border-l border-gray-200 pl-3">
      <div className="flex min-h-6 items-center gap-1">
        <p className="text-[11px] text-gray-500">{label}</p>
        {info && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-white/70 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E]/40"
                aria-label={`${label} info`}
              >
                <InfoIcon className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="end"
              className="max-w-xs text-xs leading-5"
            >
              {info}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <p
        className={cn(
          "text-sm font-medium leading-5 tabular-nums text-gray-900",
          emphasis && "font-semibold text-[#231942]",
          tone === "positive" && "text-green-700",
        )}
        style={tone === "negative" ? { color: stateOfSalesV2Colors.negative } : undefined}
      >
        {formattedValue}
      </p>
    </div>
  );
}

function ForecastLegend({
  hasExpectedCloseV2,
  hasPriorYearActual,
  historical,
}: {
  hasExpectedCloseV2: boolean;
  hasPriorYearActual: boolean;
  historical: boolean;
}) {
  return (
    <div className="mt-1 flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
      <LegendItem
        label={historical ? "Won" : hasExpectedCloseV2 ? "Revelate forecast" : "Forecast unavailable"}
        colorClassName="bg-[#1D819A]"
      />
      <LegendItem label="Target" colorClassName="bg-[#231942] opacity-50" />
      {!historical && (
        <LegendItem label="CRM forecast" colorClassName="bg-[#9F86C0]" />
      )}
      {hasPriorYearActual && (
        <div className="flex items-center gap-x-1.5">
          <div className="h-px w-4 border-t border-dashed border-gray-500" />
          <p className="text-xs text-gray-500">Actual · 12 months ago</p>
        </div>
      )}
    </div>
  );
}

function LegendItem({ label, colorClassName }: { label: string; colorClassName: string }) {
  return (
    <div className="flex items-center gap-x-1.5">
      <div className={cn("h-1 w-4 rounded-full", colorClassName)} />
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

function ForecastTooltip({
  active,
  payload,
  companyCurrency,
  future,
  historical,
}: {
  active?: boolean;
  payload?: ForecastTooltipPayload[];
  companyCurrency: Currency;
  future: boolean;
  historical: boolean;
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0].payload;

  return (
    <Card className="w-64 p-0">
      <CardHeader className="space-y-3 p-4">
        <CardTitle className="text-xs">
          {format(parse(row.month, "yyyy-MM", new Date()), "MMMM yyyy")}
        </CardTitle>
        <dl className="space-y-1.5 text-xs">
          <TooltipRow label="Target" value={row.target} companyCurrency={companyCurrency} />
          {!historical && (
            <>
              <TooltipRow label="Revelate forecast" value={row.expected_close_v2} companyCurrency={companyCurrency} />
              <TooltipRow label="CRM forecast" value={row.crm_forecast} companyCurrency={companyCurrency} />
            </>
          )}
          <TooltipRow
            label="Won"
            value={future && row.won === 0 ? null : row.won}
            companyCurrency={companyCurrency}
          />
          {row.actual_last_year !== null && (
            <TooltipRow label="Actual · 12 months ago" value={row.actual_last_year} companyCurrency={companyCurrency} />
          )}
        </dl>
      </CardHeader>
    </Card>
  );
}

function TooltipRow({
  label,
  value,
  companyCurrency,
}: {
  label: string;
  value: number | null;
  companyCurrency: Currency;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="tabular-nums">
        {value === null ? "—" : formatCompactIsoCurrency(value, companyCurrency)}
      </dd>
    </div>
  );
}
